import { BlockError } from '@/blocks.04-project/block-error/block-error';
import { LazyInit } from '@/blocks.04-project/lazy-init/lazy-init';
import reduce from 'lodash-es/reduce';
import set from 'lodash-es/set';
import get from 'lodash-es/get';
import first from 'lodash-es/first';
import last from 'lodash-es/last';


/***
 * Класс подтверждения телефона по смс
 * @example
 *
 * Аттрибуты:
 * data-send-url - {string, default = ''} - API для отправки номера телефона
 * data-confirm-url - {string, default = ''} - API для отправки кода подтверждения
 * data-timestamps - {array, default = [120, 360, 3600]} - массив временных меток для увеличение времени между запросами
 * data-status - {string, default = 'send'} - текущий статус компонента
 * data-session-id - {string, default = ''} - sessionId для бека
 *
 * Аттрибуты на элементах:
 * data-error-message - {string, default = ''} - текст ошибки для поля
 * data-input - {'send' | 'confirm' | 'confined', default = ''} - инпуты для определённых статусов
 * data-sms-submit - {'send' | 'confirm' | 'repeat'} - кнопка отправки для каждого статуса
 * data-sms-timer - {boolean, default = false} - элемент куда записывается время таймера
 * data-feedback - {boolean, default = false} - элемент куда записывается ошибка из запроса
 *
 * Классы:
 * .sms-confirm.error - из запроса пришла ошибка
 * .sms-confirm.can-repeat - можно повторить отправку смс
 * .sms-confirm[data-status="send|confirm|confirmed"] - статус компонента
 */
class SmsConfirm {
  /***
   * Конструктор класса
   * @param elem {HTMLElement} DOM элемент с классом sms-confirm
   * @param force {Boolean|undefined} принудительно инициализирование
   */
  constructor({ elem, force }) {
    this.BlockError = new BlockError({ blockName: 'SmsConfirm', destroy: this.destroy });
    if (!elem) return this.BlockError.error('Отсутствует elem');

    this.block = elem;
    this.block.SmsConfirm = this;
    this.block.AsyncSmsConfirm = new Promise((resolve) => {
      this.promiseResolve = resolve;
    }).then((ins) => ins);

    this.LazyInit = new LazyInit({ ins: this, callback: this.init, addClass: 'sms-confirm_inited' });
    this.LazyInit.init();
    if (force) this.LazyInit.earlyInit();
  }

  init = async () => {
    try {
      this.setOptions();
      this.setStatus();
      this.setBlockValidity();
      this.setValidity();

      this.block.addEventListener('click', this.clickHandler);

      this.promiseResolve(this);
      this.block.AsyncSmsConfirm = this;
    } catch (e) {
      console.error(e);
      this.destroy();
    }
  };

  /**
   * Обработка кликов блока
   * @param event {MouseEvent} - событие клика
   */
  clickHandler = (event) => {
    const send = event.target.closest('[data-sms-submit="send"]');
    if (send) {
      if (this.checkValidity()) this.fetch();
      return;
    }

    const confirm = event.target.closest('[data-sms-submit="confirm"]');
    if (confirm) {
      if (this.checkValidity()) this.fetch();
      return;
    }

    const repeat = event.target.closest('[data-sms-submit="repeat"]');
    if (repeat) {
      this.fetch({ status: 'send' });
      return;
    }
  };

  /**
   * Отправка запросов
   * @param status {Status} - статус отправки запроса
   * @returns {Promise<void>}
   */
  fetch = async ({ status = this.status } = {}) => {
    try {
      this.block.disabled = true;
      this.setError();
      const url = get(this.urls, [status]);
      const phone = get(this.inputs, 'send.input.value');
      const code = get(this.inputs, 'confirm.input')?.inputmask?.unmaskedvalue();
      const body = JSON.stringify({ phone, code, sessid: this.sessionId });
      const response = await fetch(url,{
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body
      });

      if (!response.ok) throw new Error(get(response, 'statusText', 'Возникла ошибка. Повторите попытку позже'));

      const result = get(await response.json(), 'result');
      if (!result.success) throw new Error(get(result, 'message', 'Возникла ошибка. Повторите попытку позже'));
      if (result.error) throw new Error(get(result, 'error', 'Возникла ошибка. Повторите попытку позже'));

      if (status === 'send') {
        this.setStatus({ status, next: true });
        this.setTimer();
        this.setValidity();
      } else if (status === 'confirm') {
        this.setStatus({ status, next: true });
        this.setBlockValidity({ validity: true });
        localStorage.setItem('smsConfirm', JSON.stringify({ timerCount: 0 }));
      }
    } catch (e) {
      this.setValidity();
      this.setError(e);
      this.BlockError.errorMessage(e);
    } finally {
      this.block.disabled = false;
    }
  };

  /**
   * Проверка валидности инпута
   * @param status {Status} - статус отправки
   * @returns {boolean} - валидная/не валидная
   */
  checkValidity = ({ status = this.status } = {}) => {
    const input = get(this.inputs, [status, 'input']);
    if (!input) return false;

    input.setCustomValidity('');
    const isValid = input.checkValidity();

    input.classList.toggle('invalid', !isValid);
    this.setValidity({ validity: isValid });
    return isValid;
  };

  /**
   * Установить новый статус
   * @param status {Status} - новый статус
   * @param [next=false] {Boolean} - установить следующий статус
   */
  setStatus = ({ status = this.status, next = false } = {}) => {
    let nextStatus = status;
    if (next) {
      const statuses = {
        'send': 'confirm',
        'confirm': 'confirmed',
        'confirmed': 'confirmed',
      };
      nextStatus = get(statuses, status, status);
    }

    this.status = nextStatus;
    this.block.setAttribute('data-status', this.status);
  };

  /** Установка таймера */
  setTimer = () => {
    if (this.timer) return;

    this.currentTimerValue = get(this.timestamps, this.timerCount, last(this.timestamps) || 120);
    this.timerCount += 1;
    localStorage.setItem('smsConfirm', JSON.stringify({ timerCount: this.timerCount }));
    this.block.classList.remove('can-repeat');
    if (this.timerElement) this.timerElement.innerHTML = this.currentTimerValue;

    this.timer = setInterval(() => {
      if (this.currentTimerValue === 0) {
        clearInterval(this.timer);
        this.timer = null;
        this.block.classList.add('can-repeat');

        return;
      }

      this.currentTimerValue -= 1;
      if (this.timerElement) this.timerElement.innerHTML = this.currentTimerValue;
    }, 1000);
  };

  /**
   * Установка валидности текущего инпута
   * @param [validity=false] {Boolean} - валидный ли инпут или нет
   */
  setValidity = ({ validity = false } = {}) => {
    const currentInput = get(this.inputs, [this.status, 'input']);
    const error = validity ? '' : currentInput.dataset['errorMessage'] || 'Ошибка';

    currentInput.setCustomValidity(error);
  };

  /**
   * Установка валидности всего блока для блокировки формы
   * @param [validity=false] {Boolean} - валидный ли блок
   */
  setBlockValidity = ({ validity = false } = {}) => {
    const sendInput = get(this.inputs, 'send.input');
    const confirmedInput = get(this.inputs, 'confirmed.input');

    confirmedInput.value = validity ? sendInput.value : '';
    confirmedInput.dispatchEvent(new Event('change', { bubbles: true }));
  };

  /**
   * Установки ошибки с бека
   * @param [message=''] {String} - текст ошибки
   */
  setError = ({ message = '' } = {}) => {
    const toggle = !!message;
    const text = toggle ? message : '';

    this.feedback.innerHTML = text;
    this.block.classList.toggle('error', toggle);
  };

  setOptions = () => {
    const sendUrl = this.block.dataset['sendUrl'] || '';
    if (!sendUrl) this.BlockError.throwError('Отсутствует [data-send-url]');

    const confirmUrl = this.block.dataset['confirmUrl'] || '';
    if (!confirmUrl) this.BlockError.throwError('Отсутствует [data-confirm-url]');

    this.urls = { send: sendUrl, confirm: confirmUrl };

    this.feedback = this.block.querySelector('[data-feedback]');
    if (!this.feedback) this.BlockError.throwError('Отсутствует [data-feedback]');

    this.sessionId = this.block.dataset['sessionId'] || '';

    /** @typedef {'send'|'confirm'|'confirmed'} Status - статус компонента */
    /** @type {Status} */
    this.status = 'send';

    this.inputs = reduce(this.block.querySelectorAll('[data-input]'), (res, input) => {
      const status = input.dataset['input'];
      set(res, status, { status, input });
      return res;
    }, {});
    try {
      this.timestamps = JSON.parse(this.block.dataset['timestamps']);
    } catch {
      this.timestamps = [120, 360, 3600];
    } finally {
      this.timer = null;
      this.timerCount = get(JSON.parse(localStorage.getItem('smsConfirm')), 'timerCount', 0);
      this.currentTimerValue = first(this.timestamps);
      this.timerElement = this.block.querySelector('[data-sms-timer]');
      if (!this.timerElement) this.BlockError.warnMessage('Отсутствует [data-sms-timer]');
    }
  };

  destroy = () => {
    if (this.LazyInit) this.LazyInit.destroy();
    if (this.block) {
      this.block.classList.remove('sms-confirm_inited');
      this.block.removeEventListener('click', this.clickHandler);
      delete this.block.SmsConfirm;
      delete this.block.AsyncSmsConfirm;
    }
  };

  static init({ container = document, force = false } = {}) {
    Array.from(container.querySelectorAll('.sms-confirm:not(.sms-confirm_inited)')).forEach((elem) => new SmsConfirm({ elem, force }));
  }
}

document.addEventListener('DOMContentLoaded', () => SmsConfirm.init());

// Экспорт;
window.Block = window.Block ? window.Block : {};
window.Block.SmsConfirm = SmsConfirm;
